import React, { useState, useEffect } from 'react';
import ConnectionDataTable from '@/components/data-table/ConnectionDataTable';
import { FilterOperation } from '@/utils/api';
import { StandardizedConnection } from '@/types/connections';
import { isInventorySnapshotConnectionId } from '@/shared/inventoryCsvConnectionChannels';

interface ConnectionData {
  connectionId: string | null;
  connectorId: string | null;
  availableTables: any[];
  defaultSchema: string;
  defaultTableName: string | null;
  queryResults?: any[] | null;
  selectedTableOverride?: { schema: string; tableName: string } | null;
  queryFilters?: Array<{ column: string; value: string; operation: FilterOperation }> | null;
  connectionStatus?: string;
}

interface OrganisationDataTabProps {
  organisation: any;
  connectionData: ConnectionData;
  onClearQueryFilters?: () => void;
  onConnectionChange?: (connectionId: string) => void;
}

const OrganisationDataTab: React.FC<OrganisationDataTabProps> = ({
  organisation,
  connectionData,
  onClearQueryFilters,
  onConnectionChange
}) => {
  const [availableConnections, setAvailableConnections] = useState<StandardizedConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [connectionsInitialized, setConnectionsInitialized] = useState(false);
  const [selectedConnectionStatus, setSelectedConnectionStatus] = useState<string | null>(null);

  const normalizeConnections = (connections: Record<string, any>): StandardizedConnection[] => {
    if (!connections) return [];

    const standardizedConnections = Object.entries(connections).flatMap(([key, value]: [string, any]) => {
      const resolvedConnectionId = value.connectionId || value.id || key;
      const isCsvConnection = value.service === 'csv' || value.type === 'csv' || value.type === 'CSV' || value.id?.startsWith('csv-import-');

      // Check if this connection has multiple csvImports - create separate entry for each
      if (isCsvConnection && value.csvImports && Array.isArray(value.csvImports) && value.csvImports.length > 0) {
        return value.csvImports.map((csvImport: any, index: number) => {
          const isInventoryImport = isInventorySnapshotConnectionId(csvImport.id);
          const resolvedCsvImportId =
            csvImport.id ||
            csvImport.snapshot_connection_id ||
            `${value.id || resolvedConnectionId}__csv_import_${index}`;

          return {
            id: resolvedCsvImportId,
            name: csvImport.fileName || "CSV Import",
            status: value.status || 'connected',
            logoUrl: isInventoryImport
              ? "https://via.placeholder.com/40?text=INV"
              : "https://via.placeholder.com/40?text=CSV",
            service: 'csv',
            connectorId: value.connectorId,
            description: isInventoryImport
              ? `Inventory file: ${csvImport.fileName}`
              : `CSV file: ${csvImport.fileName || 'Unnamed'}`,
            connectionId: resolvedCsvImportId,
            type: 'csv',
            created_at: csvImport.date || value.created_at || new Date().toISOString(),
            metadata: {
              file_name: csvImport.fileName,
              import_date: csvImport.date,
              parent_connection_id: value.id,
              csv_import_id: csvImport.id,
              snapshot_connection_id: csvImport.snapshot_connection_id,
              csv_import_index: index
            },
            file_name: csvImport.fileName,
            import_type: value.import_type,
            ecommerce_subtype: value.ecommerce_subtype
          } as StandardizedConnection;
        });
      }

      // For non-CSV or connections without csvImports array, return single entry
      return [{
        id: value.id || resolvedConnectionId,
        name: isCsvConnection
          ? (value.file_name || "CSV Import")
          : (value.name || 'Data Connection'),
        status: value.status || 'connected',
        logoUrl: isCsvConnection
          ? "https://via.placeholder.com/40?text=CSV"
          : (value.logoUrl || `https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/${value.service}.svg`),
        service: isCsvConnection ? 'csv' : (value.service || 'other'),
        connectorId: value.connectorId,
        description: isCsvConnection
          ? `CSV file: ${value.file_name || 'Unnamed'}`
          : (value.description || `${value.service || 'Data'} integration`),
        connectionId: resolvedConnectionId,
        type: value.type || (isCsvConnection ? 'csv' : value.service || 'other'),
        created_at: value.created_at || new Date().toISOString(),
        metadata: {
          ...(value.metadata || {}),
          file_name: value.file_name,
          import_date: value.import_date
        },
        file_name: value.file_name,
        import_type: value.import_type,
        ecommerce_subtype: value.ecommerce_subtype
      } as StandardizedConnection];
    });

    // Filter out file storage connections and keep only data connections
    const dataConnections = standardizedConnections.filter(conn => {
      // Keep CSV connections (they contain structured data)
      if (conn.service === 'csv' || conn.type === 'csv' || conn.type === 'CSV') {
        return true;
      }

      // Exclude file storage connections
      if (conn.service === 'file') {
        return false;
      }

      // Exclude non-data file types
      const nonDataTypes = ['PDF', 'pdf', 'Image', 'image', 'Document', 'document', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];
      if (nonDataTypes.includes(conn.type)) {
        return false;
      }

      // Keep database connections and other data sources
      return conn.service !== 'file' && conn.name !== "Data Connection";
    });

    return dataConnections.sort((a, b) => {
      if (a.service === 'csv' && b.service !== 'csv') return -1;
      if (a.service !== 'csv' && b.service === 'csv') return 1;
      return 0;
    });
  };

  useEffect(() => {
    if (organisation?.connections) {
      const normalizedConnections = normalizeConnections(organisation.connections);
      setAvailableConnections(normalizedConnections);
      setConnectionsInitialized(true);
    }
  }, [organisation]);

  useEffect(() => {
    if (!connectionsInitialized || availableConnections.length === 0) {
      // Clear connection data when no connections are available
      if (connectionsInitialized && availableConnections.length === 0) {
        setSelectedConnectionId(null);
        if (onConnectionChange) {
          onConnectionChange('');
        }
      }
      return;
    }

    const existingConnectionId = connectionData.connectionId;
    const csvConnection = availableConnections.find(conn => conn.service === 'csv');
    const firstConnection = availableConnections[0];

    // Use connectionId consistently to match SelectItem values
    const defaultConnectionId = existingConnectionId ||
      (csvConnection?.connectionId || csvConnection?.id) ||
      (firstConnection.connectionId || firstConnection.id);

    setSelectedConnectionId(defaultConnectionId);

    const selectedConnectionObj = availableConnections.find(
      conn => conn.connectionId === defaultConnectionId ||
        conn.connectorId === defaultConnectionId ||
        conn.id === defaultConnectionId
    );

    if (selectedConnectionObj) {
      setSelectedConnectionStatus(selectedConnectionObj.status);
    }

    if (onConnectionChange) {
      onConnectionChange(defaultConnectionId);
    }
  }, [connectionsInitialized, availableConnections, connectionData.connectionId, onConnectionChange]);

  if (!organisation?.connections || Object.keys(organisation.connections).length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="text-center p-8">
          <p className="text-gray-500">No connection data available. Please connect your data source first.</p>
        </div>
      </div>
    );
  }

  if (availableConnections.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="text-center p-8">
          <p className="text-gray-500">No data connections found. Please connect a CSV or database source.</p>
        </div>
      </div>
    );
  }

  const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const getConnectionStatusForId = (connectionId: string | null): string | null => {
    if (!connectionId) return null;

    const connection = availableConnections.find(
      conn => conn.connectionId === connectionId || conn.connectorId === connectionId
    );

    return connection?.status || null;
  };

  return (
    <div data-testid="organisation-data-tab" className="bg-white rounded-lg shadow p-6 mt-6 space-y-6">
      {selectedConnectionId ? (
        <ConnectionDataTable
          organisation={organisation}
          connectionData={{
            ...connectionData,
            connectionId: selectedConnectionId,
            connectorId: selectedConnectionId,
            connectionStatus: getConnectionStatusForId(selectedConnectionId)
          }}
          onClearQueryFilters={onClearQueryFilters}
          availableConnections={{
            connections: availableConnections,
            selectedConnectionId: selectedConnectionId,
            onConnectionChange: (connectionId: string) => {
              setSelectedConnectionId(connectionId);
              setSelectedConnectionStatus(getConnectionStatusForId(connectionId));
              if (onConnectionChange) {
                onConnectionChange(connectionId);
              }
            },
            capitalizeFirstLetter
          }}
        />
      ) : (
        <div className="text-center p-8">
          <p className="text-gray-500">Select a connection to view data.</p>
        </div>
      )}
    </div>
  );
};

export default OrganisationDataTab;
